import { useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';

import { subscribeToNewsletter } from '../lib/newsletter';
import { ApiError } from '../lib/apiClient';
import { SOCIAL_LINKS, CONTACT_EMAIL } from '../config/env';
import './SiteFooter.css';

/**
 * The site footer.
 *
 * This is where the role cards used to live on the homepage. Organizers, artists,
 * venues, agencies and sponsors are a small fraction of the people who land on Voleto,
 * and giving them five cards in the homepage body made a ticketing site read as a
 * multi-audience pitch. In a footer they are still one click away for the people who
 * want them and invisible to everyone else.
 *
 * The Invest pointer moved here for the same reason, plus a stronger one: a casual
 * visitor should experience Voleto as a ticketing platform first. Keeping a securities
 * offering out of the primary visual field is also the right side of the line that
 * 506(c) already draws.
 *
 * TWO SHAPES. During a purchase (see BUYING_FLOW in PublicLayout) the footer collapses
 * to a wordmark, contact and copyright. The reasoning is the same one that strips the
 * header nav there: a full column of links to other parts of the platform is competition
 * for someone who is mid checkout.
 */

const EXPLORE = [
  { to: '/', label: 'Home' },
  { to: '/events', label: 'Events' },
  { to: '/artists', label: 'Artists' },
];

/**
 * Workspace entry points. These are behind a role guard, so a signed out visitor
 * clicking one lands on sign in, which is the correct funnel rather than a dead end.
 */
const INDUSTRY = [
  { to: '/organizer', label: 'Organizers' },
  { to: '/artist', label: 'Artists' },
  { to: '/venue', label: 'Venues' },
  { to: '/agency', label: 'Agencies' },
  { to: '/sponsor', label: 'Sponsors' },
  { to: '/invest', label: 'Investors' },
];

function Subscribe() {
  const [email, setEmail] = useState('');
  const [state, setState] = useState<'idle' | 'sending' | 'done'>('idle');
  const [error, setError] = useState<string | null>(null);

  async function submit(event: FormEvent) {
    event.preventDefault();
    setState('sending');
    setError(null);
    try {
      await subscribeToNewsletter(email);
      setState('done');
    } catch (err: unknown) {
      setError(
        err instanceof ApiError && err.message === 'invalid_email'
          ? 'That does not look like an email address.'
          : err instanceof ApiError && err.message === 'undeliverable_address'
            ? 'That address cannot receive mail.'
            : 'Could not subscribe. Please try again.',
      );
      setState('idle');
    }
  }

  if (state === 'done') {
    return (
      <p className="footer__subscribed" role="status">
        You are on the list. We will let you know when something is worth your time.
      </p>
    );
  }

  return (
    <form className="footer__subscribe" onSubmit={submit}>
      <label className="footer__subscribe-label" htmlFor="footer-email">
        Get new events in your inbox
      </label>
      <div className="footer__subscribe-row">
        <input
          id="footer-email"
          className="footer__subscribe-input"
          type="email"
          required
          autoComplete="email"
          placeholder="you@example.com"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
        />
        <button type="submit" className="button button--primary" disabled={state === 'sending'}>
          {state === 'sending' ? 'Joining' : 'Join'}
        </button>
      </div>
      {error && <p className="footer__subscribe-error">{error}</p>}
      <p className="footer__subscribe-note">
        Event announcements only. No selling your address on.
      </p>
    </form>
  );
}

export function SiteFooter({ minimal = false }: { minimal?: boolean }) {
  const year = new Date().getFullYear();

  if (minimal) {
    return (
      <footer className="footer footer--minimal">
        <div className="container footer__minimal-inner">
          <span className="footer__mark display">Voleto&trade;</span>
          <span className="footer__minimal-links">
            <a href={`mailto:${CONTACT_EMAIL}`}>Contact</a>
            <span className="footer__copy">© {year} Voleto</span>
          </span>
        </div>
      </footer>
    );
  }

  return (
    <footer className="footer">
      <div className="container footer__inner">
        <div className="footer__brand">
          <span className="footer__mark display">Voleto&trade;</span>
          <p className="footer__tagline">The operating system for live entertainment.</p>

          {/* Rendered only when a handle is actually configured. See SOCIAL_LINKS. */}
          {SOCIAL_LINKS.length > 0 && (
            <ul className="footer__social">
              {SOCIAL_LINKS.map((s) => (
                <li key={s.label}>
                  <a href={s.url} target="_blank" rel="noreferrer noopener">
                    {s.label}
                  </a>
                </li>
              ))}
            </ul>
          )}
        </div>

        <nav className="footer__column" aria-labelledby="footer-explore">
          <h2 id="footer-explore" className="footer__heading">Explore</h2>
          <ul>
            {EXPLORE.map((item) => (
              <li key={item.to}><Link to={item.to}>{item.label}</Link></li>
            ))}
            <li><a href={`mailto:${CONTACT_EMAIL}`}>Contact</a></li>
            {/* Reachable without starting a checkout. A legal document only linked from
                inside a payment flow is one most people can never find again. */}
            <li><Link to="/terms">Terms of Service</Link></li>
          </ul>
        </nav>

        <nav className="footer__column" aria-labelledby="footer-industry">
          <h2 id="footer-industry" className="footer__heading">For the industry</h2>
          <ul>
            {INDUSTRY.map((item) => (
              <li key={item.to}><Link to={item.to}>{item.label}</Link></li>
            ))}
          </ul>
        </nav>

        <div className="footer__column footer__column--wide">
          <Subscribe />
        </div>
      </div>

      <div className="footer__bar">
        <div className="container footer__bar-inner">
          <span className="footer__copy">© {year} Voleto</span>
          <span className="footer__legal">
            Investment offerings are available to verified accredited investors only.
          </span>
        </div>
      </div>
    </footer>
  );
}
